<template>
  <v-dialog v-model="widoczny" max-width="1100" scrollable>
    <v-card v-if="sprawa">
      <v-card-title class="d-flex align-center">
        <div>
          <div class="subtitle-1 font-weight-medium">
            {{ sprawa.driver.firstName }} {{ sprawa.driver.lastName }}
            <span class="caption grey--text">#{{ sprawa.driver.numericId }}</span>
          </div>
          <div class="caption grey--text">
            {{ sprawa.proceduraNazwa }}
            <span v-if="sprawa.urzadSlug"> · {{ sprawa.urzadSlug }}</span>
            <span v-if="sprawa.driver.citizenshipName"> · {{ sprawa.driver.citizenshipName }}</span>
          </div>
        </div>
        <v-spacer />
        <v-chip small :color="kolorStatusu(sprawa.status)" text-color="white">
          {{ sprawa.statusNazwa }}
        </v-chip>
      </v-card-title>

      <v-card-text>
        <!-- Ostrzeżenia silnika reguł: najważniejsza rzecz na ekranie -->
        <v-alert
          v-if="sprawa.ostrzezenia && sprawa.ostrzezenia.length"
          type="warning"
          text dense
          class="mb-3"
        >
          <div class="font-weight-medium mb-1">
            System nie ręczy za kompletność tej listy:
          </div>
          <ul class="pl-4 mb-0">
            <li v-for="(o, i) in sprawa.ostrzezenia" :key="i" class="caption">{{ o }}</li>
          </ul>
        </v-alert>

        <!-- Kompletność -->
        <v-card outlined class="rounded-lg mb-4">
          <v-card-text class="py-3">
            <div class="d-flex align-center flex-wrap">
              <div class="mr-6">
                <div class="text-h5 font-weight-bold">{{ k.procent }}%</div>
                <div class="caption grey--text">
                  {{ k.zalatwionych }} z {{ k.obowiazkowych }} pozycji
                </div>
              </div>
              <div class="flex-grow-1">
                <v-progress-linear
                  :value="k.procent"
                  height="8"
                  rounded
                  :color="k.mozeByc100 ? 'success' : 'orange'"
                />
                <div class="caption grey--text mt-1">
                  <template v-if="k.mozeByc100">
                    Według obecnie skonfigurowanych reguł dokumentacja jest kompletna.
                    Zgodność z wymogami urzędu ocenia pracownik.
                  </template>
                  <template v-else>
                    Procent jest informacyjny — nie zastępuje sprawdzenia przez człowieka.
                  </template>
                </div>
              </div>
              <div v-if="k.odKierowcy" class="ml-6 text-center">
                <div class="text-h6 font-weight-bold orange--text">{{ k.odKierowcy }}</div>
                <div class="caption grey--text">czekamy od kierowcy</div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Wezwania z urzędu -->
        <v-card
          v-if="sprawa.wezwania && sprawa.wezwania.length"
          outlined
          class="rounded-lg mb-4 wezwanie-card"
        >
          <v-card-title class="subtitle-1">
            <v-icon left small color="error">mdi-email-alert-outline</v-icon>
            Wezwania z urzędu
          </v-card-title>
          <v-card-text>
            <div v-for="w in sprawa.wezwania" :key="w.id" class="wezwanie">
              <div class="d-flex align-center">
                <div class="flex-grow-1">
                  <span class="font-weight-medium">
                    Wezwanie z {{ data(w.otrzymanoDnia) }}
                  </span>
                  <span v-if="w.sygnatura" class="caption grey--text ml-2">
                    sygn. {{ w.sygnatura }}
                  </span>
                  <div v-if="w.organ" class="caption grey--text">{{ w.organ }}</div>
                  <div v-if="w.opis" class="caption">{{ w.opis }}</div>
                </div>
                <div class="text-right">
                  <div v-if="w.terminOdpowiedzi" class="caption font-weight-medium error--text">
                    odpowiedź do {{ data(w.terminOdpowiedzi) }}
                  </div>
                  <v-btn x-small text :disabled="!canEdit" @click="usunWezwanie(w)">
                    wycofaj
                  </v-btn>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <div class="d-flex mb-4">
          <v-btn small outlined color="error" :disabled="!canEdit" @click="otworzWezwanie">
            <v-icon left small>mdi-email-alert-outline</v-icon>Dodaj wezwanie z urzędu
          </v-btn>
          <v-btn small outlined class="ml-2" :disabled="!canEdit" @click="otworzZatrudnienie">
            <v-icon left small>mdi-briefcase-edit-outline</v-icon>
            {{ sprawa.stanowisko ? 'Warunki pracy: ' + sprawa.stanowisko : 'Uzupełnij warunki pracy' }}
          </v-btn>
        </div>

        <!-- Checklista -->
        <div v-for="grupa in grupy" :key="grupa.klucz" class="mb-4">
          <div class="section-head">
            {{ grupa.tytul }}
            <span class="caption grey--text">({{ grupa.pozycje.length }})</span>
          </div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th style="width: 30%">Dokument</th>
                <th style="width: 12%">Kto dostarcza</th>
                <th style="width: 18%">Stan</th>
                <th>Dlaczego jest wymagany</th>
                <th style="width: 40px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in grupa.pozycje" :key="p.id">
                <td>
                  <div class="font-weight-medium">{{ p.nazwa }}</div>
                  <div v-if="p.dokumentNazwa" class="caption grey--text">
                    <v-icon x-small color="success">mdi-paperclip</v-icon>
                    {{ p.dokumentNazwa }}
                    <span v-if="p.dokumentWaznoscDo">
                      (do {{ data(p.dokumentWaznoscDo) }})
                    </span>
                  </div>
                  <div v-if="p.notatka" class="caption blue--text">{{ p.notatka }}</div>
                </td>
                <td>
                  <span class="caption">{{ ktoNazwa(p.ktoDostarcza) }}</span>
                  <div v-if="p.podpis && p.podpis !== 'BRAK'" class="caption grey--text">
                    podpis: {{ ktoNazwa(p.podpis) }}
                  </div>
                </td>
                <td>
                  <v-select
                    :value="p.status"
                    :items="statusyPozycji"
                    item-text="nazwa"
                    item-value="kod"
                    dense hide-details outlined
                    class="status-select"
                    :disabled="!canEdit || zapisywanie === p.id"
                    @change="(v) => zmienStatus(p, v)"
                  />
                </td>
                <td class="caption">
                  {{ p.dlaczego }}
                  <div v-if="p.zrodloUrl" class="mt-1">
                    <a :href="p.zrodloUrl" target="_blank" rel="noopener" class="caption">
                      źródło wymagania
                    </a>
                    <span v-if="p.zestawWersja" class="grey--text">
                      · wersja reguł {{ p.zestawWersja }}
                    </span>
                  </div>
                  <div v-if="p.zrodlo === 'RECZNE'" class="caption orange--text">
                    Pozycja dołożona ręcznie
                  </div>
                  <div v-if="p.zrodlo === 'WEZWANIE'" class="caption error--text font-weight-medium">
                    Żąda tego urząd — nie znika przy przeliczeniu reguł
                  </div>
                </td>
                <td>
                  <v-btn icon x-small :disabled="!canEdit" @click="otworzNotatke(p)">
                    <v-icon x-small>mdi-note-edit-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>

        <v-btn small text color="primary" :disabled="!canEdit" @click="dialogRecznej = true">
          <v-icon left small>mdi-plus</v-icon>Dołóż pozycję ręcznie
        </v-btn>

        <!-- Czas pracy: bez niego marża na usłudze jest tylko domysłem -->
        <v-divider class="my-4" />
        <div class="subtitle-2 mb-1">
          Czas pracy nad sprawą
          <span v-if="czas.minutyRazem" class="grey--text">
            — razem {{ godziny(czas.minutyRazem) }}
          </span>
        </div>
        <p v-if="!czas.wpisy.length" class="caption grey--text mb-2">
          Nikt jeszcze nie zapisał czasu. Bez tego nie wiadomo, czy ta usługa się opłaca.
        </p>
        <v-simple-table v-else dense class="mb-2">
          <tbody>
            <tr v-for="w in czas.wpisy" :key="w.id">
              <td style="width: 110px">{{ formatDate(w.dzien) }}</td>
              <td style="width: 90px">{{ godziny(w.minuty) }}</td>
              <td>
                {{ w.opis || '—' }}
                <span v-if="w.kto" class="caption grey--text">· {{ w.kto }}</span>
              </td>
              <td class="text-right" style="width: 40px">
                <v-btn icon x-small :disabled="!canEdit" @click="usunCzas(w)">
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-row dense align="center">
          <v-col cols="4" md="2">
            <v-text-field
              v-model.number="nowyCzas.minuty"
              type="number" min="1" max="1440"
              label="Minuty" outlined dense hide-details
            />
          </v-col>
          <v-col cols="8" md="6">
            <v-text-field
              v-model="nowyCzas.opis"
              label="Co robiłeś (np. kompletowanie dokumentów)"
              outlined dense hide-details
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-btn small color="primary" :loading="zapisCzasu" :disabled="!canEdit || !nowyCzas.minuty"
              @click="dopiszCzas">
              <v-icon left small>mdi-timer-plus-outline</v-icon>Dopisz czas
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="flex-wrap">
        <v-btn small outlined :loading="przeliczanie" :disabled="!canEdit" @click="przelicz">
          <v-icon left small>mdi-refresh</v-icon>Przelicz z reguł
        </v-btn>
        <v-select
          :value="sprawa.status"
          :items="statusySprawy"
          item-text="nazwa"
          item-value="kod"
          dense hide-details outlined
          class="ml-2 status-select-wide"
          label="Status sprawy"
          :disabled="!canEdit"
          @change="zmienStatusSprawy"
        />
        <v-btn small outlined class="ml-2" :loading="pobieranieKarty" @click="pobierzKarte">
          <v-icon left small>mdi-file-document-outline</v-icon>Dane do wniosku
        </v-btn>
        <v-btn small outlined class="ml-2" :loading="pobieranieTeczki" @click="pobierzTeczke">
          <v-icon left small>mdi-folder-zip-outline</v-icon>Generuj teczkę
        </v-btn>
        <v-btn small outlined class="ml-2" :loading="pobieranieWniosku" @click="pobierzWniosek">
          <v-icon left small>mdi-file-sign</v-icon>Wypełnij wniosek
        </v-btn>
        <v-spacer />
        <v-btn
          small
          color="success"
          :loading="weryfikacja"
          :disabled="!canEdit || sprawa.status === 'ZWERYFIKOWANA'"
          @click="zweryfikuj"
        >
          <v-icon left small>mdi-check-decagram</v-icon>
          {{ sprawa.status === 'ZWERYFIKOWANA' ? 'Sprawdzona' : 'Oznacz jako sprawdzoną' }}
        </v-btn>
        <v-btn small text @click="widoczny = false">Zamknij</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Wezwanie z urzędu -->
    <v-dialog v-model="dialogWezwania" max-width="640" persistent scrollable>
      <v-card>
        <v-card-title class="subtitle-1">Wezwanie z urzędu</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="wezwanie.otrzymanoDnia" type="date"
                label="Data otrzymania *" outlined dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="wezwanie.terminOdpowiedzi" type="date"
                label="Termin odpowiedzi" outlined dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="wezwanie.sygnatura" label="Sygnatura" outlined dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="wezwanie.organ" label="Organ" outlined dense hide-details />
            </v-col>
            <v-col cols="12">
              <v-textarea v-model="wezwanie.opis" label="Czego żąda urząd (własnymi słowami)"
                outlined dense rows="2" hide-details />
            </v-col>
          </v-row>

          <div class="section-head mt-4">Dokumenty żądane w wezwaniu</div>
          <div v-for="(d, i) in wezwanie.dokumenty" :key="i" class="d-flex align-center mb-2">
            <v-text-field v-model="d.nazwa" label="Nazwa dokumentu" outlined dense hide-details />
            <v-select v-model="d.ktoDostarcza" :items="ktoOpcje" item-text="text" item-value="value"
              label="Kto" outlined dense hide-details class="ml-2" style="max-width: 160px" />
            <v-btn icon small class="ml-1" @click="wezwanie.dokumenty.splice(i, 1)">
              <v-icon small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-btn x-small text color="primary" @click="wezwanie.dokumenty.push({ nazwa: '', ktoDostarcza: 'KIEROWCA' })">
            <v-icon left x-small>mdi-plus</v-icon>Dodaj pozycję
          </v-btn>

          <p class="caption grey--text mt-3 mb-0">
            Te pozycje trafią na checklistę oznaczone jako żądanie urzędu i nie znikną
            przy przeliczeniu reguł. Termin odpowiedzi stanie się terminem sprawy.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="zapisWezwania" @click="dialogWezwania = false">Anuluj</v-btn>
          <v-btn color="error" :loading="zapisWezwania" :disabled="!wezwanie.otrzymanoDnia"
            @click="zapiszWezwanie">Dodaj wezwanie</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Warunki pracy -->
    <v-dialog v-model="dialogZatrudnienia" max-width="560">
      <v-card>
        <v-card-title class="subtitle-1">Warunki pracy w tej sprawie</v-card-title>
        <v-card-text>
          <p class="caption grey--text">
            Wniosek opisuje warunki z chwili składania, dlatego trzymamy je przy sprawie,
            a nie przy kierowcy. Reguły mogą po nich pytać.
          </p>
          <v-text-field v-model="zatrudnienie.stanowisko" label="Stanowisko" outlined dense class="mb-2" hide-details />
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="zatrudnienie.wynagrodzenieZl" type="number"
                label="Wynagrodzenie brutto (zł)" outlined dense hide-details />
            </v-col>
            <v-col cols="6">
              <v-select v-model="zatrudnienie.wymiarCzasu"
                :items="['pełny etat', '1/2 etatu', '3/4 etatu', 'umowa zlecenie', 'inny']"
                label="Wymiar czasu pracy" outlined dense hide-details clearable />
            </v-col>
            <v-col cols="7">
              <v-text-field v-model="zatrudnienie.pracodawcaNazwa" label="Pracodawca" outlined dense hide-details />
            </v-col>
            <v-col cols="5">
              <v-text-field v-model="zatrudnienie.pracodawcaNip" label="NIP" outlined dense hide-details />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="zapisZatrudnienia" @click="dialogZatrudnienia = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="zapisZatrudnienia" @click="zapiszZatrudnienie">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ręczna pozycja -->
    <v-dialog v-model="dialogRecznej" max-width="520">
      <v-card>
        <v-card-title class="subtitle-1">Dołóż pozycję do checklisty</v-card-title>
        <v-card-text>
          <v-text-field v-model="recznaNazwa" label="Nazwa dokumentu" outlined dense />
          <v-text-field v-model="recznaDlaczego" label="Dlaczego jest potrzebny" outlined dense hide-details />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogRecznej = false">Anuluj</v-btn>
          <v-btn color="primary" :disabled="!recznaNazwa" @click="dodajReczna">Dodaj</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  name: "LegalizationCaseDialog",
  props: {
    value: { type: Boolean, default: false },
    caseId: { type: String, default: null },
    canEdit: { type: Boolean, default: true },
  },
  data() {
    return {
      sprawa: null,
      statusySprawy: [],
      statusyPozycji: [],
      przeliczanie: false,
      weryfikacja: false,
      pobieranieTeczki: false,
      pobieranieKarty: false,
      pobieranieWniosku: false,
      zapisywanie: null,
      dialogRecznej: false,
      dialogWezwania: false,
      dialogZatrudnienia: false,
      zapisWezwania: false,
      zapisZatrudnienia: false,
      wezwanie: { otrzymanoDnia: '', terminOdpowiedzi: '', sygnatura: '', organ: '', opis: '', dokumenty: [] },
      zatrudnienie: { stanowisko: '', wynagrodzenieZl: '', wymiarCzasu: null, pracodawcaNazwa: '', pracodawcaNip: '' },
      ktoOpcje: [
        { text: 'Kierowca', value: 'KIEROWCA' },
        { text: 'BTP', value: 'BTP' },
        { text: 'Pracodawca', value: 'PRACODAWCA' },
        { text: 'Urząd', value: 'URZAD' },
      ],
      recznaNazwa: "",
      recznaDlaczego: "",
      czas: { minutyRazem: 0, wpisy: [] },
      nowyCzas: { minuty: null, opis: "" },
      zapisCzasu: false,
    };
  },
  computed: {
    widoczny: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
    k() {
      return (this.sprawa && this.sprawa.kompletnosc) || {};
    },
    /** Najpierw to, co blokuje: braki, potem reszta. */
    grupy() {
      const w = (this.sprawa && this.sprawa.wymagania) || [];
      const brakujace = ["BRAK", "WYMAGA_AKTUALIZACJI", "DO_WYGENEROWANIA", "DO_PODPISU", "OCZEKUJE", "DO_WERYFIKACJI"];
      return [
        { klucz: "brak", tytul: "Do załatwienia", pozycje: w.filter((p) => brakujace.includes(p.status)) },
        { klucz: "gotowe", tytul: "Gotowe", pozycje: w.filter((p) => ["ZNALEZIONY_W_BTP", "ZATWIERDZONY"].includes(p.status)) },
        { klucz: "nd", tytul: "Nie dotyczy", pozycje: w.filter((p) => p.status === "NIE_DOTYCZY") },
      ].filter((g) => g.pozycje.length);
    },
  },
  watch: {
    caseId: {
      immediate: true,
      handler(v) {
        if (v) this.pobierz();
      },
    },
  },
  methods: {
    getConfig() {
      return { headers: { Authorization: "Bearer " + localStorage.getItem("token") } };
    },
    godziny(minuty) {
      if (!minuty) return "0 min";
      const h = Math.floor(minuty / 60);
      const m = minuty % 60;
      return h ? `${h} h${m ? ' ' + m + ' min' : ''}` : `${m} min`;
    },
    async pobierzCzas() {
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/worklog",
          this.getConfig()
        );
        this.czas = res.data.data || { minutyRazem: 0, wpisy: [] };
      } catch (e) {
        // Brak czasu pracy nie może zasłonić samej sprawy — pokazujemy pustą listę.
        this.czas = { minutyRazem: 0, wpisy: [] };
      }
    },
    async dopiszCzas() {
      this.zapisCzasu = true;
      try {
        await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/worklog",
          { caseId: this.caseId, minuty: this.nowyCzas.minuty, opis: this.nowyCzas.opis },
          this.getConfig()
        );
        this.nowyCzas = { minuty: null, opis: "" };
        await this.pobierzCzas();
      } catch (e) {
        this.blad(e, "Nie zapisano czasu pracy");
      } finally {
        this.zapisCzasu = false;
      }
    },
    async usunCzas(wpis) {
      try {
        await axios.delete(
          process.env.VUE_APP_ROOT_API + "legalization/worklog/" + wpis.id,
          this.getConfig()
        );
        await this.pobierzCzas();
      } catch (e) {
        this.blad(e, "Nie usunięto wpisu");
      }
    },
    async pobierz() {
      try {
        const [sprawa, slowniki] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId, this.getConfig()),
          axios.get(process.env.VUE_APP_ROOT_API + "legalization/case-dictionaries", this.getConfig()),
        ]);
        this.sprawa = sprawa.data.data;
        this.statusySprawy = slowniki.data.data.statusySprawy;
        this.statusyPozycji = slowniki.data.data.statusyPozycji;
        await this.pobierzCzas();
      } catch (e) {
        this.blad(e, "Nie udało się pobrać sprawy");
      }
    },
    async przelicz() {
      this.przeliczanie = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/recalculate",
          {},
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.$emit("zmieniono");
      } catch (e) {
        this.blad(e, "Nie udało się przeliczyć");
      } finally {
        this.przeliczanie = false;
      }
    },
    /** Teczka i karta danych schodzą z serwera jako pliki — bierzemy je blobem. */
    async pobierzPlik(sciezka, domyslnaNazwa, flaga) {
      this[flaga] = true;
      try {
        const res = await axios.get(
          process.env.VUE_APP_ROOT_API + sciezka,
          { ...this.getConfig(), responseType: "blob" }
        );
        const naglowek = res.headers["content-disposition"] || "";
        const dopasowanie = /filename\*=UTF-8''([^;]+)/.exec(naglowek);
        const nazwa = dopasowanie ? decodeURIComponent(dopasowanie[1]) : domyslnaNazwa;
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = nazwa;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        // Błąd przychodzi jako blob — trzeba go odczytać, żeby pokazać powód.
        let powod = "";
        try {
          powod = JSON.parse(await e.response.data.text()).message;
        } catch (x) {
          powod = "";
        }
        Swal.fire("Nie udało się pobrać pliku", powod, "error");
      } finally {
        this[flaga] = false;
      }
    },
    pobierzTeczke() {
      return this.pobierzPlik(
        "legalization/cases/" + this.caseId + "/package",
        "teczka.zip",
        "pobieranieTeczki"
      );
    },
    async pobierzWniosek() {
      // Mapowanie pól druku nie zostało jeszcze obejrzane przez człowieka —
      // mówimy o tym wprost ZA KAŻDYM razem, a nie drobnym drukiem.
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "Wypełniony wniosek urzędowy",
        html:
          "System wypełni tylko te rubryki, które potrafi wypełnić z danych w BTP " +
          "(dane osobowe, paszport, adres). <b>Reszta zostanie pusta</b> — formularz " +
          "pyta też o rzeczy, których nie przechowujemy.<br><br>" +
          "Przed wysłaniem do urzędu <b>przejrzyj każdą stronę</b>.",
        showCancelButton: true,
        confirmButtonText: "Pobierz",
        cancelButtonText: "Anuluj",
      });
      if (!isConfirmed) return;
      return this.pobierzPlik(
        "legalization/cases/" + this.caseId + "/application-form",
        "wniosek.pdf",
        "pobieranieWniosku"
      );
    },
    pobierzKarte() {
      return this.pobierzPlik(
        "legalization/cases/" + this.caseId + "/data-sheet",
        "dane-do-wniosku.pdf",
        "pobieranieKarty"
      );
    },
    async zmienStatus(pozycja, status) {
      this.zapisywanie = pozycja.id;
      try {
        const res = await axios.patch(
          process.env.VUE_APP_ROOT_API + "legalization/requirements/" + pozycja.id,
          { status },
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.$emit("zmieniono");
      } catch (e) {
        this.blad(e, "Nie udało się zapisać stanu pozycji");
      } finally {
        this.zapisywanie = null;
      }
    },
    async otworzNotatke(pozycja) {
      const { value, isConfirmed } = await Swal.fire({
        title: "Notatka do pozycji",
        input: "textarea",
        inputValue: pozycja.notatka || "",
        showCancelButton: true,
        confirmButtonText: "Zapisz",
        cancelButtonText: "Anuluj",
      });
      if (!isConfirmed) return;
      try {
        const res = await axios.patch(
          process.env.VUE_APP_ROOT_API + "legalization/requirements/" + pozycja.id,
          { notatka: value },
          this.getConfig()
        );
        this.sprawa = res.data.data;
      } catch (e) {
        this.blad(e, "Nie udało się zapisać notatki");
      }
    },
    otworzWezwanie() {
      const dzis = new Date().toISOString().slice(0, 10);
      this.wezwanie = {
        otrzymanoDnia: dzis, terminOdpowiedzi: '', sygnatura: '', organ: '', opis: '',
        dokumenty: [{ nazwa: '', ktoDostarcza: 'KIEROWCA' }],
      };
      this.dialogWezwania = true;
    },
    async zapiszWezwanie() {
      this.zapisWezwania = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/summons",
          { ...this.wezwanie, dokumenty: this.wezwanie.dokumenty.filter((d) => d.nazwa) },
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.dialogWezwania = false;
        this.$emit("zmieniono");
      } catch (e) {
        this.blad(e, "Nie zapisano wezwania");
      } finally {
        this.zapisWezwania = false;
      }
    },
    async usunWezwanie(w) {
      const { isConfirmed } = await Swal.fire({
        icon: "warning",
        title: "Wycofać wezwanie?",
        text: "Zniknie razem z pozycjami checklisty, które z niego wynikają. Pozycje z reguł zostaną.",
        showCancelButton: true,
        confirmButtonText: "Wycofaj",
        cancelButtonText: "Anuluj",
      });
      if (!isConfirmed) return;
      try {
        const res = await axios.delete(
          process.env.VUE_APP_ROOT_API + "legalization/summons/" + w.id,
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.$emit("zmieniono");
      } catch (e) {
        this.blad(e, "Nie udało się wycofać wezwania");
      }
    },
    otworzZatrudnienie() {
      this.zatrudnienie = {
        stanowisko: this.sprawa.stanowisko || "",
        wynagrodzenieZl: this.sprawa.wynagrodzenieGr ? (this.sprawa.wynagrodzenieGr / 100).toFixed(2) : "",
        wymiarCzasu: this.sprawa.wymiarCzasu || null,
        pracodawcaNazwa: this.sprawa.pracodawcaNazwa || "",
        pracodawcaNip: this.sprawa.pracodawcaNip || "",
      };
      this.dialogZatrudnienia = true;
    },
    async zapiszZatrudnienie() {
      this.zapisZatrudnienia = true;
      try {
        const zl = parseFloat(String(this.zatrudnienie.wynagrodzenieZl).replace(",", "."));
        const res = await axios.patch(
          process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/employment",
          {
            stanowisko: this.zatrudnienie.stanowisko,
            // W bazie kwoty są w groszach — tak jak wszędzie w BTP.
            wynagrodzenieGr: isNaN(zl) ? null : Math.round(zl * 100),
            wymiarCzasu: this.zatrudnienie.wymiarCzasu,
            pracodawcaNazwa: this.zatrudnienie.pracodawcaNazwa,
            pracodawcaNip: this.zatrudnienie.pracodawcaNip,
          },
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.dialogZatrudnienia = false;
        this.$emit("zmieniono");
      } catch (e) {
        this.blad(e, "Nie zapisano warunków pracy");
      } finally {
        this.zapisZatrudnienia = false;
      }
    },
    async dodajReczna() {
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/requirements",
          { nazwa: this.recznaNazwa, dlaczego: this.recznaDlaczego },
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.dialogRecznej = false;
        this.recznaNazwa = "";
        this.recznaDlaczego = "";
      } catch (e) {
        this.blad(e, "Nie udało się dodać pozycji");
      }
    },
    async zmienStatusSprawy(status) {
      try {
        const res = await axios.patch(
          process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/status",
          { status },
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.$emit("zmieniono");
      } catch (e) {
        this.blad(e, "Nie udało się zmienić statusu");
        this.pobierz();
      }
    },
    async zweryfikuj() {
      this.weryfikacja = true;
      try {
        const res = await axios.post(
          process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/verify",
          {},
          this.getConfig()
        );
        this.sprawa = res.data.data;
        this.$emit("zmieniono");
        Swal.fire({ icon: "success", title: "Sprawa sprawdzona", timer: 1400, showConfirmButton: false });
      } catch (e) {
        // Backend nie pozwala zatwierdzić sprawy z brakami bez jawnej zgody —
        // pytamy wprost, zamiast po cichu przepychać.
        const powod = (e.response && e.response.data && e.response.data.message) || "";
        const { isConfirmed } = await Swal.fire({
          icon: "warning",
          title: "Sprawa nie jest kompletna",
          text: powod + " Czy mimo to bierzesz odpowiedzialność za komplet dokumentów?",
          showCancelButton: true,
          confirmButtonText: "Biorę odpowiedzialność",
          cancelButtonText: "Anuluj",
        });
        if (isConfirmed) {
          try {
            const res = await axios.post(
              process.env.VUE_APP_ROOT_API + "legalization/cases/" + this.caseId + "/verify",
              { potwierdzamBraki: true },
              this.getConfig()
            );
            this.sprawa = res.data.data;
            this.$emit("zmieniono");
          } catch (e2) {
            this.blad(e2, "Nie udało się oznaczyć sprawy");
          }
        }
      } finally {
        this.weryfikacja = false;
      }
    },
    blad(e, tytul) {
      Swal.fire(tytul, (e.response && e.response.data && e.response.data.message) || "", "error");
    },
    data(v) {
      return v ? new Date(v).toLocaleDateString("pl-PL") : "—";
    },
    ktoNazwa(kod) {
      return { KIEROWCA: "Kierowca", BTP: "BTP", PRACODAWCA: "Pracodawca", URZAD: "Urząd", BRAK: "—" }[kod] || kod;
    },
    kolorStatusu(status) {
      if (["DECYZJA_NEGATYWNA", "WEZWANIE"].includes(status)) return "error";
      if (["ZWERYFIKOWANA", "DECYZJA_POZYTYWNA", "ZAKONCZONA"].includes(status)) return "success";
      if (["ZLOZONA", "OCZEKIWANIE_NA_URZAD"].includes(status)) return "blue";
      return "blue-grey";
    },
  },
};
</script>

<style scoped>
.section-head {
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a8f98;
  margin-bottom: 4px;
}
.wezwanie-card {
  border-color: rgba(244, 67, 54, 0.4);
}
.wezwanie {
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.wezwanie:last-child {
  border-bottom: none;
}
.status-select {
  max-width: 190px;
}
.status-select-wide {
  max-width: 260px;
}
</style>
